import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

/**
 * Global guard: any authenticated player without a nickname is bounced to
 * `/profile?need=nickname&next=<current path>` so the pseudo is enforced
 * BEFORE any code purchase / reservation / play action.
 *
 * Admins are exempt — they don't compete on leaderboards.
 * The guard is a no-op on `/profile`, `/login`, `/auth/*`, `/checkout/return`
 * so the nickname setup screen itself can render normally.
 */
const EXEMPT_PREFIXES = [
  "/profile",
  "/login",
  "/auth",
  "/checkout/return",
  // Live Events use their own per-event pseudo system (chosen inside the
  // LiveJoin form). Players don't need a global platform nickname to join
  // a room — the guard must not hijack /live/:code/* routes.
  "/live",
];

export default function RequireNickname() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // user === null → still loading; user === false → anonymous; object → logged in
    if (!user || user === false) return;
    if (user.role === "admin") return;
    if (user.nickname && String(user.nickname).trim().length >= 3) return;
    const path = location.pathname || "/";
    if (EXEMPT_PREFIXES.some((p) => path.startsWith(p))) return;
    const next = path + (location.search || "");
    navigate(`/profile?need=nickname&next=${encodeURIComponent(next)}`, {
      replace: true,
    });
  }, [user, location.pathname, location.search, navigate]);

  return null;
}
